"use client";
import {
    IAccountAssets,
    IBlacklistUrls,
    IBlocks,
    ITheme,
    IUserData,
} from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import Link from "next/link";
import AdminPopover from "../../content/AdminPopover";
import { getTextColorForBackground, shadow } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import BlockToggle from "./tabs/toggle";
import SocialButtonsOptions from "./tabs/socialButtons/options";
import SocialButtonsCustomization from "./tabs/socialButtons/customization";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import RenderIconOrAsset from "../RenderIconOrAsset";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme: ITheme;
    newBlock?: string | null;
    blackListUrls?: IBlacklistUrls[] | null;
}
export default function BlockSocialButtons({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
    blackListUrls,
}: Props) {
    const BlockClickHandler = (
        button: React.MouseEvent,
        block: IBlocks,
        icon_id: string
    ) => {
        if ((button.button == 0 && !admin) || (button.button == 1 && !admin)) {
            fetch("/api/user/click", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id: user.id,
                    block_id: block.id,
                    child_block_id: icon_id,
                }),
            });
        }
    };
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Multi Link",
            description:
                "A row of buttons with icons that you can customize to your liking",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <SocialButtonsOptions
                        key={block.id}
                        account={user}
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={assets}
                    />
                    <SocialButtonsCustomization
                        block={blockUpdate}
                        update={handleUpdate}
                    />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    let backgroundStyle = {};
    if (blockUpdate.colour.toggle && blockUpdate.colour.background_block) {
        backgroundStyle = {
            background: blockUpdate.colour.background_block,
        };
    }
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Multi Link"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div
                key={block.id}
                // justify-start - Left
                // justify-center - center
                // justify-end - right
                className={`flex w-full flex-row flex-wrap gap-2 p-2 ${user.theme.button_rounded} ${blockUpdate.colour.position == "center" ? "justify-center" : blockUpdate.colour.position == "right" ? "justify-end" : "justify-start"}`}
                style={backgroundStyle}
                id="multiLinkFlex"
            >
                {/* MAP OF ICONS */}
                {blockUpdate.options?.icons?.map((icon: any) => {
                    let new_style = style;
                    if (icon.style.enabled) {
                        new_style = {
                            background: icon.style.background,
                            color: icon.style.text
                                ? icon.style.text
                                : getTextColorForBackground(
                                      icon.style.background
                                  ),
                        };
                    }
                    const contentWarning = blackListUrls?.find((e) =>
                        icon.url?.includes(e.find)
                    );
                    if (icon.adult || contentWarning) {
                        return (
                            <Dialog key={icon.id}>
                                <DialogTrigger asChild>
                                    <button
                                        style={new_style}
                                        id="multiLinkButton"
                                        className={`{formatClassName(text)} 
                    ${shadow(theme.button_shadow)}
                    ${theme?.button_rounded}
                    flex p-2 text-xl transition-all duration-150 hover:scale-110
                    ${block.name.replaceAll(" ", "-")}
                    `}
                                    >
                                        <RenderIconOrAsset
                                            account_id={user.id}
                                            iconName={icon.icon}
                                            size={26}
                                            raw
                                        />
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="sm:h-fit">
                                    <DialogHeader>
                                        <DialogTitle>
                                            NSFW or mature content ahead
                                        </DialogTitle>
                                        <DialogDescription>
                                            Where you're about to go has been
                                            flagged as mature or NSFW
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="flex flex-col gap-4">
                                        <p className="w-full rounded-md bg-accent p-1 text-center text-sm text-foreground/80">
                                            {icon.url.replace("https://", "")}
                                        </p>
                                        <Separator />
                                        <Label variant={"toggle"}>
                                            Would you like to continue?
                                        </Label>
                                        <div className="flex w-full items-center gap-2">
                                            <Link
                                                href={icon.url}
                                                target="_blank"
                                                prefetch={false}
                                                onClick={(e) => {
                                                    BlockClickHandler(
                                                        e,
                                                        block,
                                                        icon.id
                                                    );
                                                }}
                                                onAuxClick={(e) => {
                                                    BlockClickHandler(
                                                        e,
                                                        block,
                                                        icon.id
                                                    );
                                                }}
                                            >
                                                <Button>Proceed</Button>
                                            </Link>
                                            <DialogClose>
                                                <Button variant={"outline"}>
                                                    Go back
                                                </Button>
                                            </DialogClose>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        );
                    }
                    return (
                        <Link
                            href={icon.url}
                            target="_blank"
                            prefetch={false}
                            key={icon.id}
                            onClick={(e) => {
                                BlockClickHandler(e, block, icon.id);
                            }}
                            onAuxClick={(e) => {
                                BlockClickHandler(e, block, icon.id);
                            }}
                        >
                            <button
                                style={new_style}
                                id="multiLinkButton"
                                className={`{formatClassName(text)} 
                    ${shadow(theme.button_shadow)}
                    ${theme?.button_rounded}
                    flex p-2 text-xl transition-all duration-150 hover:scale-110
                    ${block.name.replaceAll(" ", "-")}
                    `}
                            >
                                <RenderIconOrAsset
                                    account_id={user.id}
                                    iconName={icon.icon}
                                    size={26}
                                    raw
                                />
                            </button>
                        </Link>
                    );
                })}
            </div>
        </AdminPopover>
    );
}
